import { useEffect, useState } from "react";
import { Server, TrendingUp, Check } from "lucide-react";
import { api } from "../lib/api.js";
import { PageHeader, Card, Spinner, StatusPill } from "../components/ui.jsx";
import PlanMatrix from "../components/PlanMatrix.jsx";

// Admin billing: what the infra actually costs (Hetzner) + the customer plans
// and the margin on each. Data from /api/billing.
export default function Billing() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Declared with the other hooks, ABOVE the early returns below — a useState placed after
  // them runs conditionally, which breaks the rules of hooks and blanks the page.
  const [retiring, setRetiring] = useState(null);

  const load = () => {
    api.billing().then(setData).catch(setError);
  };
  useEffect(() => {
    let cancelled = false;
    api.billing().then((d) => { if (!cancelled) setData(d); }).catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, []);

  if (error) return <div className="page"><p style={{ color: "var(--err)" }}>Failed to load billing: {error.message}</p></div>;
  if (!data) return <div className="flex h-40 items-center justify-center" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2" /> Loading…</div>;

  // Defensive defaults: a malformed /api/billing payload must not blank the page.
  const infra = { servers: [], totalMonthly: 0, totalHourly: 0, ...(data.infra || {}) };
  if (!Array.isArray(infra.servers)) infra.servers = [];
  const computePlans = Array.isArray(data.computePlans) ? data.computePlans : [];
  const dbPlans = Array.isArray(data.dbPlans) ? data.dbPlans : [];
  const mailPlans = Array.isArray(data.mailPlans) ? data.mailPlans : [];
  const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
  const usd = (n) => `$${Number(n || 0)}`;
  const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

  return (
    <div className="page">
      <PageHeader title="Billing & Plans" subtitle="What your infrastructure costs, and what you charge for it." />

      {retiring && (
        <RetireServerModal
          server={retiring}
          onClose={() => setRetiring(null)}
          onDone={() => { setRetiring(null); load?.(); }}
        />
      )}

      {/* Infra spend */}
      <Card className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2" style={{ color: "var(--text)" }}>
            <Server size={16} /> <span className="font-semibold text-sm">Infrastructure spend (Hetzner)</span>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold" style={{ color: "var(--text)" }}>{eur(infra.totalMonthly)}<span className="text-sm font-normal" style={{ color: "var(--text-muted)" }}>/mo</span></div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>{eur(infra.totalHourly)}/hr · {infra.servers.length} server{infra.servers.length !== 1 ? "s" : ""}</div>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="w-full text-sm" style={{ minWidth: 520, borderCollapse: "collapse" }}>
            <thead><tr style={{ color: "var(--text-muted)", textAlign: "left" }}>
              <th className="py-2 pr-4 font-semibold">Server</th><th className="py-2 pr-4 font-semibold">Type</th>
              <th className="py-2 pr-4 font-semibold">Region</th><th className="py-2 pr-4 font-semibold">Status</th>
              <th className="py-2 pr-4 font-semibold">€/hr</th><th className="py-2 pr-4 font-semibold">€/mo</th>
              <th className="py-2 font-semibold"></th>
            </tr></thead>
            <tbody>
              {infra.servers.map((s) => (
                <tr key={s.name} style={{ borderTop: "1px solid var(--border)" }}>
                  <td className="py-2 pr-4" style={{ color: "var(--text)" }}>{s.name}</td>
                  <td className="py-2 pr-4 mono" style={{ color: "var(--text-muted)" }}>{s.type} · {s.cores}vCPU/{s.memory}GB</td>
                  <td className="py-2 pr-4" style={{ color: "var(--text-muted)" }}>{s.location}</td>
                  <td className="py-2 pr-4"><StatusPill status={(s.status || "").split(":")[0]} /></td>
                  <td className="py-2 pr-4 mono" style={{ color: "var(--text-muted)" }}>{eur(s.hourly)}</td>
                  <td className="py-2 mono" style={{ color: "var(--text)" }}>{eur(s.monthly)}</td>
                  <td className="py-2 text-right">
                    <button
                      className="btn btn-danger"
                      style={{ padding: "2px 8px", fontSize: 12 }}
                      onClick={() => setRetiring(s)}
                      title={`Snapshot and delete ${s.name}`}
                    >Retire</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Compute plans */}
      <div className="flex items-center gap-2 mb-3" style={{ color: "var(--text)" }}>
        <TrendingUp size={16} /> <span className="font-semibold text-sm">Compute plans</span>
      </div>
      <div className="mb-6">
        <PlanMatrix plans={computePlans} showCost={true} />
      </div>

      {/* DB plans */}
      <div className="flex items-center gap-2 mb-3" style={{ color: "var(--text)" }}>
        <TrendingUp size={16} /> <span className="font-semibold text-sm">Managed Postgres add-ons</span>
      </div>
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))" }}>
        {dbPlans.map((p) => (
          <Card key={p.id} className="card-hover">
            <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>{p.name}</div>
            <div className="mt-1 text-2xl font-bold" style={{ color: "var(--text)" }}>{usd(p.priceMo)}<span className="text-sm font-normal" style={{ color: "var(--text-muted)" }}>/mo</span></div>
            <div className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>{p.ram} RAM · {p.storage} storage</div>
            <div className="mt-3 text-xs" style={{ color: "var(--ok-text)" }}>margin {eur(p.marginMo)}/mo · {p.discountVsRenderPct}% under Render</div>
          </Card>
        ))}
      </div>

      {/* Email hosting (priced in GBP per mailbox) */}
      {mailPlans.length > 0 && (
        <>
          <div className="flex items-center gap-2 mt-6 mb-3" style={{ color: "var(--text)" }}>
            <TrendingUp size={16} /> <span className="font-semibold text-sm">Email hosting (per mailbox)</span>
          </div>
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))" }}>
            {mailPlans.map((p) => (
              <Card key={p.id} className="card-hover">
                <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>{p.name}</div>
                <div className="mt-1 text-2xl font-bold" style={{ color: "var(--text)" }}>{gbp(p.priceGbp)}<span className="text-sm font-normal" style={{ color: "var(--text-muted)" }}>/mo</span></div>
                <div className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>{p.storageGb} GB · {p.note}</div>
                <div className="mt-3 flex flex-col gap-1 text-xs">
                  <span style={{ color: "var(--text-muted)" }}>cost {gbp(p.costGbp)}/mo</span>
                  <span style={{ color: "var(--ok-text)" }}>margin {gbp(p.marginGbp)}/mo ({p.marginPct}%)</span>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Retiring a server is irreversible, so the dialog is built to slow you down in exactly
// one place: you type the server's name. That is the same gate the API enforces
// (hetzner.assertDeletable) — the UI just makes it obvious rather than a surprise 400.
function RetireServerModal({ server, onClose, onDone }) {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const matches = typed === server.name;

  const go = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.deleteHetznerServer(server.id ?? server.hetznerId, { confirm_name: typed, reason });
      setResult(r);
    } catch (e) { setErr(e); } finally { setBusy(false); }
  };

  return (
    <div
      role="dialog" aria-modal="true" aria-label={`Retire ${server.name}`}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <Card style={{ maxWidth: 520, width: "100%" }}>
        {result ? (
          <>
            <div className="font-semibold mb-2" style={{ color: "var(--text)" }}>{server.name} deleted</div>
            <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>
              Saving {eur(result.deleted?.monthlySaved || 0)}/mo.
              {result.snapshot?.id
                ? ` A snapshot was taken first (image ${result.snapshot.id}) — restore from it in the Hetzner Console.`
                : " No snapshot was taken."}
            </p>
            <button className="btn btn-primary" onClick={onDone}>Done</button>
          </>
        ) : (
          <>
            <div className="font-semibold mb-1" style={{ color: "var(--err)" }}>Retire {server.name}?</div>
            <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>
              This deletes the server at Hetzner. It cannot be undone. A snapshot is taken first, so
              you can rebuild from it — snapshots are billed on space used, usually pennies.
              <br /><br />
              {server.type} · {server.location} · {server.ip} · <strong>{eur(server.monthly)}/mo</strong>
            </p>
            <label className="text-xs" style={{ color: "var(--text-muted)" }}>
              Type <span className="mono" style={{ color: "var(--text)" }}>{server.name}</span> to confirm
              <input className="input mt-1" value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
            </label>
            <label className="text-xs mt-3 block" style={{ color: "var(--text-muted)" }}>
              Reason (recorded in the audit log)
              <input className="input mt-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="unused since 6 Aug" />
            </label>
            {err && <div className="text-xs mt-2" style={{ color: "var(--err)" }}>{err.message}</div>}
            <div className="flex gap-2 mt-4">
              <button className="btn btn-danger" disabled={!matches || busy} onClick={go}>
                {busy ? "Retiring…" : "Snapshot and delete"}
              </button>
              <button className="btn" disabled={busy} onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
